import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../api';

const toEdges = (links) => links.map((l) => ({ source: l.source.id || l.source, target: l.target.id || l.target, type: l.type, label: l.label }));

function usePanZoom() {
  const [v, setV] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef(null);
  const onWheel = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - r.left; const my = e.clientY - r.top;
    setV((s) => {
      const k = Math.min(4, Math.max(0.5, s.k * (e.deltaY < 0 ? 1.12 : 0.89)));
      return { k, x: mx - ((mx - s.x) * k) / s.k, y: my - ((my - s.y) * k) / s.k };
    });
  };
  const onMouseDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, moved: false }; };
  const onMouseMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx; const dy = e.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    d.sx = e.clientX; d.sy = e.clientY;
    setV((s) => ({ ...s, x: s.x + dx, y: s.y + dy }));
  };
  const end = () => { const m = drag.current?.moved; drag.current = null; return m; };
  return { v, reset: () => setV({ k: 1, x: 0, y: 0 }), handlers: { onWheel, onMouseDown, onMouseMove, onMouseLeave: end }, end };
}

const GraphCanvas = forwardRef(function GraphCanvas({ root, nodes, links, styleId, onSelect, onGroups }, ref) {
  const { design } = useOutletContext();
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const pz = usePanZoom();
  useImperativeHandle(ref, () => ({ fit: pz.reset }));

  useEffect(() => {
    if (!root || !nodes.length) return;
    setLoading(true);
    const graph = { root, nodes: nodes.map(({ x, y, vx, vy, index, __indexColor, ...n }) => n), edges: toEdges(links) };
    api.post('/cs/graph/render', { graph, style: styleId, theme: design === 'rosso' ? 'dark' : 'light' })
      .then((r) => { setRes(r.data); onGroups?.(r.data.groups || []); }).catch(() => setRes(null)).finally(() => setLoading(false));
  }, [root, nodes, links, styleId, design]); // eslint-disable-line react-hooks/exhaustive-deps

  const click = (e) => {
    if (pz.end()) return;
    const id = e.target.closest('[data-id]')?.getAttribute('data-id');
    if (id) onSelect(id);
  };
  return (
    <div className="absolute inset-0 overflow-hidden cursor-grab active:cursor-grabbing select-none" style={{ background: res?.bg }} {...pz.handlers} onMouseUp={click} data-testid="graph-svg-canvas">
      {res?.svg && (
        <div style={{ transform: `translate(${pz.v.x}px, ${pz.v.y}px) scale(${pz.v.k})`, transformOrigin: '0 0' }}
          className="w-full h-full [&>svg]:w-full [&>svg]:h-full" dangerouslySetInnerHTML={{ __html: res.svg }} />
      )}
      {loading && <Loader2 className="absolute top-4 left-4 animate-spin text-[#D4AF37]" size={18} data-testid="graph-svg-loading" />}
    </div>
  );
});

export default GraphCanvas;
